import { Button, Input } from '@atlas/ui-primitives'
import { type InputHTMLAttributes, type ReactNode, useId } from 'react'
import { FieldShell } from '../field-shell/field-shell'

/**
 * Configuration for an optional inline action button within a {@link Field}.
 *
 * @property label - Button text (e.g., "Clear", "Show")
 * @property onPress - Optional click handler
 *
 * @example
 * ```tsx
 * const clearAction: FieldAction = {
 *   label: 'Clear',
 *   onPress: () => setValue('')
 * }
 * ```
 */
export interface FieldAction {
	label: string
	onPress?: () => void
}

/**
 * Props for the {@link Field} component.
 *
 * Extends native HTML input attributes with custom props for label, description, and action.
 *
 * @property label - Visible label text (required, linked to input via htmlFor)
 * @property description - Optional helper text below input (linked via aria-describedby)
 * @property action - Optional inline button (demonstrates primitives composition)
 * @property className - Optional Tailwind classes for the container
 * @property type - HTML input type (text, email, password, etc.)
 * @property placeholder - Placeholder text
 * @property value - Controlled value
 * @property onChange - Change handler
 * @property required - Native HTML required attribute
 * @property disabled - Native HTML disabled attribute
 *
 * @example
 * ```tsx
 * <Field
 *   label="Email"
 *   type="email"
 *   placeholder="you@example.com"
 *   description="We'll never share your email"
 * />
 * ```
 */
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'size'> {
	/** Visible, accessible label associated with the input. */
	label: string
	/** Optional helper text linked to the input via `aria-describedby`. */
	description?: ReactNode
	/** Optional inline action rendered as a primitives-layer Button. */
	action?: FieldAction
}

/**
 * Labeled text input molecule.
 *
 * @remarks
 * - **Composition:** a {@link FieldShell} cell over the `<Input>` and `<Button>`
 *   primitives, so the label element, its styling and the description slot are the
 *   design system's single copy, and the control is the same input every other
 *   surface uses — same surface colour, placeholder token and focus ring.
 * - **Accessibility:** Auto-generated unique ID links label, input, and description.
 *   Uses `htmlFor` and `aria-describedby` for screen reader support.
 * - **Styling:** All colors, spacing, and borders use design tokens from `@atlas/tokens`.
 * - **Action Button:** Optional inline button (size: 'sm', variant: 'muted') for
 *   actions like "Clear" or "Show password".
 *
 * @example Basic usage
 * ```tsx
 * import { Field } from '@atlas/ui-components'
 *
 * function LoginForm() {
 *   return (
 *     <form>
 *       <Field
 *         label="Email"
 *         type="email"
 *         placeholder="you@example.com"
 *         required
 *       />
 *       <Field
 *         label="Password"
 *         type="password"
 *         description="At least 8 characters"
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With inline action button
 * ```tsx
 * const [query, setQuery] = useState('')
 *
 * <Field
 *   label="Search"
 *   value={query}
 *   onChange={(e) => setQuery(e.target.value)}
 *   action={{
 *     label: 'Clear',
 *     onPress: () => setQuery('')
 *   }}
 * />
 * ```
 *
 * @see packages/ui-components/src/field/field.stories.tsx for visual examples
 * @see {@link FieldAction} for action button configuration
 */
export function Field({ label, description, action, className, ...inputProps }: FieldProps) {
	const id = useId()
	const descriptionId = description ? `${id}-description` : undefined
	return (
		<FieldShell label={label} htmlFor={id} description={description} className={className}>
			<div className="flex items-center gap-2">
				<Input id={id} aria-describedby={descriptionId} className="flex-1" {...inputProps} />
				{action ? (
					<Button size="sm" variant="muted" onPress={action.onPress}>
						{action.label}
					</Button>
				) : null}
			</div>
		</FieldShell>
	)
}
